import { ReactNode, useEffect } from 'react';
import { Container } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Header } from './Header';
import { TweaksPanel } from '../components/TweaksPanel';
import {
  FilterSidebarProvider,
  FilterSidebarOutlet,
  useHasFilterSidebar,
  useFilterSidebarCollapsed,
} from './FilterSidebarContext';

export interface DashboardLayoutProps {
  children: ReactNode;
  /** Legacy prop, non più usato dal nuovo layout (header ha menu orizzontale).
   *  Mantenuto opzionale per evitare di rompere i caller in attesa di migrazione. */
  viewTitle?: string;
  viewSubtitle?: string;
}

/**
 * SP-8a — Nuova shell dashboard.
 *
 * Struttura:
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ HEADER (logo + menu orizzontale + utility)                   │
 *   ├──────────┬───────────────────────────────────────────────────┤
 *   │ FILTRI   │ CONTENUTO pagina                                  │
 *   │ sidebar  │ (full-width quando la sidebar é vuota)              │
 *   └──────────┴───────────────────────────────────────────────────┘
 *
 * - La sidebar filtri (`<FilterSidebarOutlet />`) é alimentata via
 *   `FilterSidebarProvider` + hook `useFilterSidebar` da ogni pagina.
 * - Quando nessuna pagina ha registrato slot, la colonna sidebar collassa
 *   (display: none) e il contenuto occupa tutto.
 * - Su mobile (<992px) la sidebar é nascosta in overlay (toggle separato
 *   non implementato in SP-8a — rimandato a iterazione successiva).
 */
export default function DashboardLayout({ children }: DashboardLayoutProps) {
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-layout', 'vertical');
    root.setAttribute('data-sidebar', 'light');
    if (!root.getAttribute('data-sidebar-size')) root.setAttribute('data-sidebar-size', 'lg');
    if (!root.getAttribute('data-bs-theme')) root.setAttribute('data-bs-theme', 'light');
    root.setAttribute('data-topbar', 'light');
    if (!root.getAttribute('data-layout-mode')) root.setAttribute('data-layout-mode', 'light');
    root.setAttribute('data-layout-style', 'default');
  }, []);

  return (
    <FilterSidebarProvider>
      <div className="vp-shell">
        <Header />
        <div className="vp-shell__body">
          <FilterSidebarPanel />
          <main className="vp-shell__main">
            <Container fluid className="py-3">
              <FilterToggle />
              {children}
            </Container>
          </main>
        </div>
        <TweaksPanel />
      </div>
    </FilterSidebarProvider>
  );
}

/**
 * Sidebar filter container — renderizza l'outlet con classi/aria appropriati,
 * o `null` quando nessuno slot é registrato (cosí la grid collassa).
 */
function FilterSidebarPanel() {
  const hasSidebar = useHasFilterSidebar();
  const { collapsed } = useFilterSidebarCollapsed();
  const { t } = useTranslation('shell');
  // SP-8g §7 — nascosta se vuota OPPURE collassata manualmente (la grid
  // collassa a 1 colonna e il contenuto occupa tutto).
  if (!hasSidebar || collapsed) return null;
  return (
    <aside
      className="vp-filter-sidebar"
      aria-label={t('filters.aria')}
    >
      <h2 className="vp-filter-sidebar__title">
        <i className="bi bi-funnel me-2" />{t('filters.title')}
      </h2>
      <FilterSidebarOutlet />
    </aside>
  );
}

/**
 * SP-8g §7 — pulsante mostra/nascondi sidebar filtri, reso nell'area
 * contenuto. Visibile solo quando una pagina ha registrato filtri e solo su
 * desktop (≥992px): sotto, la sidebar è già nascosta di default (SP-7c).
 */
function FilterToggle() {
  const hasSidebar = useHasFilterSidebar();
  const { collapsed, toggle } = useFilterSidebarCollapsed();
  const { t } = useTranslation('shell');
  if (!hasSidebar) return null;
  return (
    <button
      type="button"
      className="btn btn-sm btn-light d-none d-lg-inline-flex align-items-center mb-2"
      onClick={toggle}
      aria-pressed={!collapsed}
      title={collapsed ? t('filters.show') : t('filters.hide')}
    >
      <i className={`bi ${collapsed ? 'bi-funnel' : 'bi-funnel-fill'} me-1`} />
      {collapsed ? t('filters.show') : t('filters.hide')}
    </button>
  );
}
